import { useId, useState } from 'react';
import { intentionHistory, type AnyEvent, type ItemRecord } from '@intentionalist/shared';
import { fieldValue } from '@intentionalist/shared';
import { InvestPrompt } from './InvestPrompt.js';
import { setIntentionWith } from './mutations.js';

/**
 * The record of what has been decided about an item (Epic 5).
 *
 * This was the usage panel, and held how often a thing had been used alongside
 * what had been decided about it. Recording each use turned out to be more
 * bookkeeping than the product earns -- the point is to be deliberate about what
 * you own, not to keep a diary of wearing it -- so the counting is gone and the
 * deciding is what is left.
 */
export function VerdictPanel({
  item,
  onWrite,
}: {
  item: ItemRecord;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const ids = { reason: useId(), review: useId(), verdict: useId() };
  const [reason, setReason] = useState('');
  const [reviewAgainOn, setReviewAgainOn] = useState(item.reviewAgainOn ?? '');
  /** FR-8.3: set when the verdict just recorded here was Invest. */
  const [offerEntry, setOfferEntry] = useState(false);

  const history = intentionHistory(item);

  return (
    <>
      <section aria-labelledby={ids.verdict} className="usage">
        <h3 id={ids.verdict}>Verdict</h3>

        <div className="field">
          <label htmlFor={ids.reason}>Reason</label>
          <input
            id={ids.reason}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this verdict?"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label htmlFor={ids.review}>Reconsider on</label>
          <input
            id={ids.review}
            type="date"
            value={reviewAgainOn}
            onChange={(e) => setReviewAgainOn(e.target.value)}
          />
        </div>

        <div className="actions">
          {(['keep', 'uncertain', 'release', 'invest'] as const).map((intention) => (
            <button
              key={intention}
              type="button"
              // The same control as the review session's, recorded the same way,
              // so it reads the same way. All four carry it, so none is made
              // easier to press than another (NFR-SAFE-2).
              className="primary"
              onClick={() => {
                onWrite(setIntentionWith(item.itemId, intention, { reason, reviewAgainOn }));
                // Cleared so the next verdict does not silently inherit this one's
                // reasoning, which would make the history misleading.
                setReason('');
                /*
                 * FR-8.3 is about the act of marking, not the screen it was
                 * marked on. The offer used to live on the catalog row's verdict
                 * control and in the review session, so recording Invest here --
                 * the one place dedicated to recording a verdict -- was the one
                 * place that did not offer.
                 *
                 * Offered, never created: the verdict stands on its own and the
                 * entry is a second decision (NFR-SAFE-2).
                 */
                setOfferEntry(intention === 'invest');
              }}
            >
              {intention[0]!.toUpperCase() + intention.slice(1)}
            </button>
          ))}
        </div>

        {offerEntry && (
          <InvestPrompt
            subject={{
              itemId: item.itemId,
              name: String(fieldValue(item, 'name') ?? ''),
            }}
            onWrite={onWrite}
            onDone={() => setOfferEntry(false)}
          />
        )}

        {history.length > 0 && (
          <>
            <h4>History</h4>
            {/* FR-5.4: never rewritten, so "I keep deciding to keep this" becomes
              visible over time. */}
            <ul className="log" aria-label="Intention history">
              {history.map((entry) => (
                <li key={entry.hlc}>
                  {/*
                    Two children, because a `.log` row is a left and a right.
                    The date leads, because that is what the list is for: "keep,
                    keep, keep" says nothing on its own, while "keep in March and
                    again in September" is the pattern FR-5.4 keeps a history to
                    make visible, and it cannot be read without the when.
                  */}
                  <span>
                    {/* Absent on a verdict recorded before this was kept. The
                        line still reads; it just cannot say when. */}
                    {entry.at !== undefined && (
                      <>
                        <time dateTime={entry.at}>
                          {new Date(entry.at).toLocaleDateString()}
                        </time>{' '}
                      </>
                    )}
                    <strong>{entry.intention}</strong>
                    {entry.reason !== undefined && ` — ${entry.reason}`}
                  </span>
                  {entry.reviewAgainOn !== undefined && (
                    <span className="muted">reconsider {entry.reviewAgainOn}</span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  );
}
